// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, MapPin, Search } from "lucide-react";
import { useStaffHomeLocations, useEventStaffForTravel } from "@/hooks/useTravelData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const StaffLocationsTab: React.FC = () => {
  const { data: staffList, isLoading: stLoading } = useEventStaffForTravel();
  const { data: locations, isLoading: locLoading } = useStaffHomeLocations();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [editModal, setEditModal] = useState<{ open: boolean; staffId: string; staffName: string } | null>(null);
  const [form, setForm] = useState({ city: "", state: "", nearest_airport: "", notes: "" });

  const locationMap = useMemo(() => {
    const map: Record<string, any> = {};
    (locations || []).forEach((l: any) => { map[l.staff_id] = l; });
    return map;
  }, [locations]);

  const filtered = useMemo(() => {
    return (staffList || []).filter((s: any) => {
      if (search && !s.name?.toLowerCase().includes(search.toLowerCase()) && !s.email?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [staffList, search]);

  // Group by city for the sidebar view
  const groupedByCity = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filtered.forEach((s: any) => {
      const loc = locationMap[s.id];
      const key = loc ? `${loc.city}, ${loc.state || ""}`.trim() : "No Location Set";
      if (!groups[key]) groups[key] = [];
      groups[key].push({ ...s, location: loc });
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === "No Location Set") return 1;
      if (b === "No Location Set") return -1;
      return a.localeCompare(b);
    });
  }, [filtered, locationMap]);

  const handleEdit = (staffId: string, staffName: string) => {
    const loc = locationMap[staffId];
    setForm({
      city: loc?.city || "",
      state: loc?.state || "",
      nearest_airport: loc?.nearest_airport || "",
      notes: loc?.notes || "",
    });
    setEditModal({ open: true, staffId, staffName });
  };

  const handleSave = async () => {
    if (!editModal || !form.city.trim()) { toast.error("City is required"); return; }
    const payload = {
      staff_id: editModal.staffId,
      city: form.city,
      state: form.state || null,
      nearest_airport: form.nearest_airport || null,
      notes: form.notes || null,
    };
    const { error } = await supabase.from("manage_staff_home_locations").upsert(payload, { onConflict: "staff_id" });
    if (error) { toast.error("Failed to save"); return; }
    toast.success("Location saved");
    setEditModal(null);
    queryClient.invalidateQueries({ queryKey: ["staff-home-locations"] });
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status"] });
  };

  if (stLoading || locLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const noLocationCount = filtered.filter((s: any) => !locationMap[s.id]).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end justify-between">
        <div className="relative min-w-[250px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        {noLocationCount > 0 && (
          <p className="text-sm text-yellow-600 font-medium">⚠️ {noLocationCount} staff without home location set</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Table */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Home City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Airport</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((s: any) => {
                      const loc = locationMap[s.id];
                      return (
                        <TableRow key={s.id} className={!loc ? "bg-yellow-50/50" : ""}>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell className="text-sm">{s.email || "—"}</TableCell>
                          <TableCell>{loc?.city || "—"}</TableCell>
                          <TableCell>{loc?.state || "—"}</TableCell>
                          <TableCell className="font-mono text-sm">{loc?.nearest_airport || "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant={loc ? "ghost" : "outline"} onClick={() => handleEdit(s.id, s.name)}>
                              <MapPin className="h-4 w-4 mr-1" />{loc ? "Edit" : "Set Location"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Grouped Sidebar */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Staff by City</h3>
          {groupedByCity.map(([city, members]) => (
            <Card key={city}>
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  {city}
                  <span className="text-muted-foreground font-normal ml-auto">{members.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-0.5">
                  {members.map((m: any) => (
                    <p key={m.id} className="text-sm text-muted-foreground">{m.name}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={!!editModal?.open} onOpenChange={(v) => !v && setEditModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Home Location — {editModal?.staffName}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>City *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>Nearest Airport Code</Label><Input value={form.nearest_airport} onChange={(e) => setForm({ ...form, nearest_airport: e.target.value })} placeholder="e.g. MCI, ORD" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button onClick={handleSave} className="w-full">Save Location</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffLocationsTab;
